import { DEVICE_WIDTHS } from '@/types/feedback';

import type { DevicePreset, FeedbackComment, Pin } from '@/types/feedback';

// Studio editor's own responsive breakpoints are per-site: their ids ("max",
// "variants-xxxx") don't map onto ours and their widths never line up with
// our fixed toolbar widths (DEVICE_WIDTHS) either, but they form contiguous
// min-width ranges: mobile (<= 750), desktop/"max" (>= 751, including the
// former tablet range 751-1000). Used to classify breakpoints whose id we
// don't recognize.
const STUDIO_MOBILE_MAX_WIDTH = 750;

function widthToDevicePreset(minWidth?: number | null): DevicePreset {
  if (minWidth == null) return 'desktop';
  const exact = (
    Object.entries(DEVICE_WIDTHS) as [DevicePreset, number][]
  ).find(([, w]) => w === minWidth);
  if (exact) return exact[0];
  if (minWidth <= STUDIO_MOBILE_MAX_WIDTH) return 'mobile';
  return 'desktop';
}

function breakpointToDevicePreset(
  breakpoint?: { id?: string; minWidth?: number; maxWidth?: number } | null,
): DevicePreset {
  if (!breakpoint) return 'desktop';
  const id = breakpoint.id?.toUpperCase();
  if (id === 'MOBILE') return 'mobile';
  if (id === 'DESKTOP' || id === 'TABLET') return 'desktop';
  // Unrecognized id (studio-editor's own breakpoint ids like "max" or
  // "variants-xxxx"): classify by width instead of blindly defaulting to
  // desktop, since studio's mobile breakpoints have unrecognized ids too.
  return widthToDevicePreset(breakpoint.minWidth);
}

function normalizePagePath(path?: string | null): string {
  // Strip ALL leading slashes and backslashes so a stored inner path like
  // "//evil.com" cannot survive as "/evil.com" and later become the
  // protocol-relative URL "//evil.com" at the navigation site (open-redirect).
  return (path ?? '').replace(/^[/\\]+/, '');
}

export function commentToPin(comment: FeedbackComment): Pin {
  const location = comment.commentLocation;
  const position = location?.position;
  const isLegacy = Boolean(location) && !position;

  return {
    id: comment.id ?? String(Date.now() + Math.random()),
    selector: position?.selector ?? '',
    offsetXPct: position?.offsetXPct ?? 0,
    offsetYPct: position?.offsetYPct ?? 0,
    fallbackXPct: isLegacy ? 0 : (location?.x ?? 0),
    fallbackYPct: isLegacy ? 0 : (location?.y ?? 0),
    isLegacy,
    legacyX: isLegacy ? (location?.x ?? 0) : undefined,
    legacyY: isLegacy ? (location?.y ?? 0) : undefined,
    label: comment.message ?? '',
    createdAt: comment.createdDate
      ? new Date(comment.createdDate).getTime()
      : Date.now(),
    commentId: comment.id ?? undefined,
    commenterId: comment.commenterId,
    status: comment.status,
    revision: comment.revision ?? null,
    devicePreset: breakpointToDevicePreset(location?.breakpoint),
    pagePath: normalizePagePath(position?.innerPath ?? location?.innerPath),
    replies: comment.replies,
  };
}
